import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import {
  Fields,
  Slot,
  SlotComponent,
  ComponentData,
  Data,
  renderContext,
  Render,
  usePuck,
} from '@puckeditor/core'
import ReferralStep from 'steps/referral/ReferralStep3'
import { getPuckFileUrl } from 'breaktime-theme/src/puck/fields/FileUploadField'
import {
  BackgroundStyleProps,
  BorderStyleProps,
  TypographyProps,
  FontWeight,
  backgroundStyleField,
  borderStyleField,
  fontFamilyField,
  fontFamilyFallbackField,
  textTransformField,
  fontWeightOptions,
  applyTypography,
  applyBackgroundStyle,
  applyBorderStyle,
  applyShadowStyle,
  splitBackgroundLayers,
} from 'breaktime-theme/src/puck/fields/StyleFields'
import {
  Spacing,
  defaultSpacing,
  SpacingGrid,
} from 'breaktime-theme/src/puck/fields/SpacingField'
import {
  buildCardSpacing,
} from '../ListicleContainer/ListicleContainer'
import { ThemeCustomFields } from 'breaktime-theme/src/puck/theme-config'
import { StartScreenTransition } from '../shared/StartScreen'
import {
  REFERRAL_DISALLOW,
  ReferralCardProps,
  cleanRichtext,
  getCardAction,
  referralCardStyle,
  richtextField,
} from '../ReferralCard/ReferralCard'
import {
  referralButtonStyle,
  referralButtonVars,
  shopButtonOverlay,
} from '../ReferralButton/ReferralButton'
import { borderOverlayStyle } from 'breaktime-theme/src/puck/fields/StyleFields'
import {
  getCardDropdown,
  referralDropdownStyles,
} from '../ReferralDropdown/ReferralDropdown'

// Bundle the legacy referral styles into the puck iframe so the editor preview
// matches production 1:1 (same approach as ListicleContainer).
import 'steps/referral/ReferralStep.scss'
import './referral-puck.scss'

export type ReferralLayout = 'ONE_COLUMN' | 'TWO_COLUMN' | 'THREE_COLUMN'
export type ReferralVoteType = 'NONE' | 'PER_ITEM' | 'ALL_ITEMS'

/** Single voting control: mode + bar config (uniform across cards). Vote
 * counts show in Per Item mode only (legacy screen behavior). */
export type ReferralVotingConfig = {
  mode?: ReferralVoteType
  goal?: number
  barColor?: string
  barHighlightColor?: string
  barCompleteColor?: string
}

export type ReferralContainerProps = {
  id?: string
  headline?: string
  headlineFontFamily?: TypographyProps['fontFamily']
  headlineFontFamilyFallback?: TypographyProps['fontFamilyFallback']
  headlineTextTransform?: TypographyProps['textTransform']
  headlineFontSize?: string
  headlineFontWeight?: FontWeight
  headlineColor?: string
  introCopy?: string
  bottomText?: string
  layout: ReferralLayout
  voting?: ReferralVotingConfig
  /** Free slot above the cards (legacy header image / Wistia video / anything). */
  header: Slot
  items: Slot
  /** Free slot under the bottom text (social share, extra content). */
  footer: Slot
  startScreen: Slot
  background?: BackgroundStyleProps
  cardBackground?: BackgroundStyleProps
  cardBorder?: BorderStyleProps
  cardPadding?: Spacing
  cardMargin?: Spacing
}

type ReferralCardSlotItem = {
  type: 'ReferralCard'
  props: ReferralCardProps & {
    id: string
    media?: ComponentData[]
    mediaHover?: ComponentData[]
    action?: ComponentData[]
    content?: ComponentData[]
  }
}

/** Editor grid for the cards slot — puck wraps each slot child in a div, so
 * grid tracks (not the legacy .card-list flex rules) size the columns. */
export const cardsSlotStyle = (layout: ReferralLayout): React.CSSProperties => ({
  display: 'grid',
  width: '100%',
  gridTemplateColumns:
    layout === 'THREE_COLUMN'
      ? 'repeat(3, 1fr)'
      : layout === 'TWO_COLUMN'
        ? 'repeat(2, 1fr)'
        : '1fr',
  gap: layout === 'TWO_COLUMN' ? '30px' : '20px 15px',
})

/** Container props as stored in puck data (slots are raw arrays). */
type ReferralContainerStoredProps = Omit<
  ReferralContainerProps,
  'header' | 'items' | 'footer' | 'startScreen'
> & {
  header?: ComponentData[]
  items?: ReferralCardSlotItem[]
  footer?: ComponentData[]
  startScreen?: ComponentData[]
}

export const REFERRAL_INITIAL_DATA: ReferralContainerProps = {
  headline: 'New Referral',
  introCopy: '',
  bottomText: '',
  layout: 'TWO_COLUMN',
  voting: { mode: 'NONE', goal: 100 },
  header: [],
  items: [],
  footer: [],
  startScreen: [],
  background: {},
  cardBackground: {},
  cardBorder: {},
}

const spacingFieldRender =
  (title: string) =>
  ({ value, onChange }: { value?: Spacing; onChange: (v: Spacing) => void }) =>
    (
      <SpacingGrid
        title={title}
        value={typeof value === 'object' && value ? value : defaultSpacing}
        onChange={onChange}
      />
    )

export const REFERRAL_FIELDS: Fields<
  ReferralContainerProps,
  ThemeCustomFields[keyof ThemeCustomFields]
> = {
  headline: { type: 'text', label: 'Headline' },
  headlineFontFamily: fontFamilyField,
  headlineFontFamilyFallback: fontFamilyFallbackField,
  headlineTextTransform: textTransformField,
  headlineFontSize: { type: 'text', label: 'Headline Font Size', placeholder: '24px' },
  headlineFontWeight: { type: 'select', label: 'Headline Font Weight', options: fontWeightOptions },
  headlineColor: {
    type: 'colorPicker',
    label: 'Headline Color',
    clearable: true,
  },
  introCopy: richtextField('Intro Copy'),
  bottomText: richtextField('Bottom Text'),
  layout: {
    type: 'radio',
    label: 'Layout',
    options: [
      { label: '1 Column', value: 'ONE_COLUMN' },
      { label: '2 Columns', value: 'TWO_COLUMN' },
      { label: '3 Columns', value: 'THREE_COLUMN' },
    ],
  },
  voting: {
    type: 'object',
    label: 'Voting',
    objectFields: {
      mode: {
        type: 'radio',
        label: 'Mode',
        options: [
          { label: 'None', value: 'NONE' },
          { label: 'Per Item', value: 'PER_ITEM' },
          { label: 'All Items', value: 'ALL_ITEMS' },
        ],
      },
      goal: { type: 'number', label: 'Vote Goal', min: 0 },
      barColor: { type: 'colorPicker', label: 'Bar Color', clearable: true },
      barHighlightColor: {
        type: 'colorPicker',
        label: 'Bar Highlight Color',
        clearable: true,
      },
      barCompleteColor: {
        type: 'colorPicker',
        label: 'Bar Complete Color',
        clearable: true,
      },
    },
  },
  header: { type: 'slot', label: 'Header', disallow: REFERRAL_DISALLOW },
  items: { type: 'slot', allow: ['ReferralCard'] },
  footer: { type: 'slot', label: 'Footer', disallow: REFERRAL_DISALLOW },
  startScreen: { type: 'slot', label: 'Start Screen', allow: ['PollStartScreen'] },
  background: { ...backgroundStyleField, label: 'Background' },
  cardBackground: { ...backgroundStyleField, label: 'Card Background' },
  cardBorder: { ...borderStyleField, label: 'Card Border' },
  cardPadding: {
    type: 'custom',
    label: 'Card Padding',
    render: spacingFieldRender('Padding'),
  },
  cardMargin: {
    type: 'custom',
    label: 'Card Margin',
    render: spacingFieldRender('Margin'),
  },
}

type HeadlineStyleProps = Pick<
  ReferralContainerProps,
  | 'headlineFontFamily'
  | 'headlineFontFamilyFallback'
  | 'headlineTextTransform'
  | 'headlineFontSize'
  | 'headlineFontWeight'
  | 'headlineColor'
>

export const buildHeadlineStyle = (
  p: HeadlineStyleProps,
): React.CSSProperties => ({
  ...applyTypography({
    fontFamily: p?.headlineFontFamily,
    fontFamilyFallback: p?.headlineFontFamilyFallback,
    textTransform: p?.headlineTextTransform,
  }),
  ...(p?.headlineFontSize ? { fontSize: p.headlineFontSize } : {}),
  ...(p?.headlineFontWeight
    ? { fontWeight: Number(p.headlineFontWeight) || undefined }
    : {}),
  ...(p?.headlineColor ? { color: p.headlineColor } : {}),
})

/** Last-row leftover count for the `remainder-{n}` centering classes. */
export const cardListRemainder = (
  layout: ReferralLayout,
  visibleCount: number,
): number => {
  if (layout === 'TWO_COLUMN') return visibleCount % 2
  if (layout === 'THREE_COLUMN') return visibleCount % 3
  return 0
}

type RenderCtx = React.ContextType<typeof renderContext>

/** Callbacks + context the break player passes through PuckStep metadata. */
type ReferralStepMetadata = {
  brk?: { id?: string }
  step?: { breakId?: string; [key: string]: unknown }
  onEngage?: () => void
  onAwardPoints?: () => void
  onChangeBackground?: (bg: {
    backgroundColor?: string
    backgroundImageSrc?: string
    mobileBackgroundImageSrc?: string
    backgroundSize?: string
    backgroundPosition?: string
    backgroundRepeat?: string
    backgroundOpacity?: number
  }) => void
  skipButton?: React.ReactNode
  nextStepButton?: React.ReactNode
}

/**
 * Map puck cards onto the legacy `step.links` shape consumed by ReferralStep3 /
 * ReferralCardNoVoting. Card copy + action data map to legacy fields; slots
 * travel as rendered nodes (`puckMedia`, `puckMediaHover`, `puckExtraContent`).
 */
const mapCardsToLegacyLinks = (
  cards: ReferralCardSlotItem[],
  slotContent: (items: ComponentData[]) => React.ReactNode,
  voting: ReferralVotingConfig = {},
) =>
  cards
    .filter(it => it.type === 'ReferralCard')
    .map((it, i) => {
      const cardId = it.props.id ?? `puck-ref-${i}`
      const action = getCardAction(it.props.action)
      const rawDropdown = getCardDropdown(it.props.action)
      const singleItem =
        !action && rawDropdown && (rawDropdown.props.items ?? []).length === 1
          ? (rawDropdown.props.items ?? [])[0]
          : undefined
      const dropdown = singleItem ? undefined : rawDropdown
      const votingOn = voting.mode && voting.mode !== 'NONE'
      return {
        id: cardId,
        buttonType: dropdown ? 'MENU' : 'BUTTON',
        url: singleItem ? singleItem.link : action?.props.url ?? '',
        buttonText: singleItem
          ? singleItem.label
          : action?.props.buttonText ?? '',
        // legacy showButton semantics: empty text hides the button while the
        // url keeps the card image a tracked link
        showButton: singleItem
          ? !!singleItem.label?.trim()
          : !!action?.props.buttonText?.trim(),
        // raw color for the legacy voting card / dropdown (inline styles);
        // the BUTTON card styles via puckButtonVars instead (hover support).
        // MENU cards take the dropdown's color, others the button's.
        buttonColor:
          (dropdown ? dropdown.props.buttonColor : action?.props.buttonColor) ||
          'var(--theme-color-button, #4c3371)',
        // background + hover come from referral-puck.scss via these vars
        // (falls back to --theme-color-button, i.e. the break's themeColor3)
        puckButtonVars: referralButtonVars({
          buttonColor: singleItem
            ? rawDropdown?.props.buttonColor
            : action?.props.buttonColor,
          buttonHoverColor: singleItem
            ? rawDropdown?.props.buttonHoverColor
            : action?.props.buttonHoverColor,
        }),
        puckButtonStyle: singleItem
          ? referralButtonStyle({
              fontFamily: rawDropdown?.props.fontFamily,
              fontFamilyFallback: rawDropdown?.props.fontFamilyFallback,
              borderRadius: rawDropdown?.props.borderRadius,
              textColor: rawDropdown?.props.textColor,
            })
          : action
            ? {
                ...referralButtonStyle(action.props),
                ...(shopButtonOverlay(action.props)
                  ? { position: 'relative', overflow: 'visible' }
                  : {}),
              }
            : undefined,
        puckButtonOverlayStyle: action
          ? shopButtonOverlay(action.props)
          : undefined,
        menuTextColor: dropdown?.props.textColor,
        menuHoverColor: dropdown?.props.buttonHoverColor,
        ...(dropdown ? referralDropdownStyles(dropdown.props) : {}),
        menu: dropdown
          ? {
              text: dropdown.props.menuText,
              items: (dropdown.props.items ?? []).map((item, j) => ({
                id: `${cardId}-menu-${j}`,
                label: item.label,
                link: item.link,
              })),
            }
          : undefined,
        topText: cleanRichtext(it.props.topText),
        bottomText: cleanRichtext(it.props.bottomText),
        // the container Voting object is the single control — mode turns the
        // voting screen on, bar config applies uniformly to every card
        hasGoal: !!votingOn,
        goal: votingOn ? voting.goal ?? 0 : 0,
        barColor: voting.barColor,
        barHighlightColor: voting.barHighlightColor,
        barCompleteColor: voting.barCompleteColor,
        // BR-5452/5451 AC: map the PQ hide-list to the legacy `dynamics`
        // shape so ReferralStep3's existing PQ filtering hides this card for
        // the matching quiz result (value = PQResult puck component id).
        dynamics: (
          Array.isArray(it.props.pqHideResults)
            ? it.props.pqHideResults
            : it.props.pqHideResults?.refs ?? []
        )
          .filter(r => r?.resultId)
          .map(r => ({ value: r.resultId })),
        puckCardClass: 'puck-referral-card',
        puckCardStyle: referralCardStyle(it.props),
        puckCardBorderOverlayStyle: borderOverlayStyle(it.props.borderOverlay),
        puckMedia: slotContent(it.props.media ?? []),
        puckMediaHover: slotContent(it.props.mediaHover ?? []),
        puckExtraContent: slotContent(it.props.content ?? []),
      }
    })

/**
 * Production (render-mode) path: delegates to the extended legacy
 * ReferralStep3 (isPuck + injected nodes) so analytics, engage/points, PQ
 * filtering and the exact legacy DOM/SCSS come for free.
 */
const PuckReferralContainerRenderer = ({ puckCtx }: { puckCtx: RenderCtx }) => {
  const container = puckCtx.data.content.find(
    c => c.type === 'ReferralContainer',
  )
  const containerProps = (container?.props ?? {}) as ReferralContainerStoredProps

  const {
    brk,
    step,
    onEngage,
    onAwardPoints,
    onChangeBackground,
    skipButton,
    nextStepButton,
  } = (puckCtx.metadata ?? {}) as ReferralStepMetadata

  const breakType: 'editorial' | 'sponsored' =
    (puckCtx.data.root?.props as { breakType?: 'editorial' | 'sponsored' })
      ?.breakType ?? 'editorial'

  const slotContent = useCallback(
    (items: ComponentData[]): React.ReactNode => {
      if (!items?.length) return null
      return (
        <Render
          config={puckCtx.config}
          data={{ content: items, root: { props: { breakType } } } as Data}
          // shareEventSlug: PollSocialShare blocks dropped in these slots
          // fire the referral-named share analytics event
          metadata={{ ...puckCtx.metadata, shareEventSlug: 'referral' }}
        />
      )
    },
    [puckCtx.config, breakType, puckCtx.metadata],
  )

  const layout = containerProps.layout ?? 'TWO_COLUMN'

  const links = useMemo(
    () =>
      mapCardsToLegacyLinks(
        containerProps.items ?? [],
        slotContent,
        containerProps.voting ?? {},
      ),
    [containerProps.items, slotContent, containerProps.voting],
  )

  const referralData = {
    // PUCK-BACKEND-GAP (BR-5452): no beaker referral entity id for a puck
    // step; voting is mocked
    id: undefined,
    headline: containerProps.headline,
    introCopy: cleanRichtext(containerProps.introCopy),
    bottomText: cleanRichtext(containerProps.bottomText),
    layout,
    voteType: containerProps.voting?.mode ?? 'NONE',
    headerType: 'NONE',
    headerImage: undefined,
    videoId: undefined,
  }

  // Full-bleed step background — push to the outer StepBackground via
  // metadata, same contract as poll/listicle.
  const containerBackground: BackgroundStyleProps =
    containerProps.background ?? {}
  const bgStyle = applyBackgroundStyle(containerBackground)
  const hasBackground = Object.keys(bgStyle).length > 0
  const bgImageSrc = containerBackground?.backgroundImage
    ? getPuckFileUrl(containerBackground.backgroundImage)
    : ''
  const mobileBgImageSrc = containerBackground?.mobileBackgroundImage
    ? getPuckFileUrl(containerBackground.mobileBackgroundImage)
    : ''

  const onChangeBackgroundRef = useRef(onChangeBackground)
  useEffect(() => {
    onChangeBackgroundRef.current = onChangeBackground
  }, [onChangeBackground])

  useEffect(() => {
    const cb = onChangeBackgroundRef.current
    if (!cb || !hasBackground) return
    cb({
      backgroundColor: containerBackground?.backgroundColor,
      backgroundImageSrc: bgImageSrc || undefined,
      mobileBackgroundImageSrc: mobileBgImageSrc || undefined,
      backgroundSize: containerBackground?.backgroundSize,
      backgroundPosition: containerBackground?.backgroundPosition,
      backgroundRepeat: containerBackground?.backgroundRepeat,
      backgroundOpacity: containerBackground?.backgroundImageOpacity,
    })
  }, [
    hasBackground,
    containerBackground?.backgroundColor,
    bgImageSrc,
    mobileBgImageSrc,
    containerBackground?.backgroundSize,
    containerBackground?.backgroundPosition,
    containerBackground?.backgroundRepeat,
    containerBackground?.backgroundImageOpacity,
  ])

  const cardLayers = splitBackgroundLayers(containerProps.cardBackground)
  const puckCardStyle = {
    backgroundColor: 'transparent',
    ...cardLayers.base,
    ...applyBorderStyle(containerProps.cardBorder ?? {}),
    ...buildCardSpacing(containerProps.cardPadding, containerProps.cardMargin),
  }

  const breakId = step?.breakId ?? brk?.id

  const referralStep = (
    <ReferralStep
      brk={brk}
      breakId={breakId}
      step={{
        ...(step ?? {}),
        referralData,
        links,
        // v1: social share comes from the footer slot, not the legacy flag
        socialShareOn: false,
      }}
      onEngage={onEngage ?? (() => {})}
      onAwardPoints={onAwardPoints ?? (() => {})}
      skipButton={skipButton}
      nextStepButton={nextStepButton}
      isPuck
      // PUCK-BACKEND-GAP (BR-5452): mock vote tallies (no beaker referral
      // entity behind a puck step); backend puck-voting is follow-up work.
      mockVoting
      puckCardStyle={puckCardStyle}
      puckCardOverlayStyle={cardLayers.overlay}
      puckHeadlineStyle={buildHeadlineStyle(containerProps)}
      puckHeader={slotContent(containerProps.header ?? [])}
      puckFooter={slotContent(containerProps.footer ?? [])}
    />
  )

  return (
    <StartScreenTransition
      startScreen={
        containerProps.startScreen as unknown as {
          type: string
          props?: Record<string, unknown>
        }[]
      }
    >
      {referralStep}
    </StartScreenTransition>
  )
}

const slotLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'rgba(0,0,0,0.4)',
  marginBottom: 4,
}

const PuckReferralEditorWrapper = ({
  children,
  startScreen: StartScreen,
  header: Header,
  footer: Footer,
  headline,
  headlineStyle,
  introCopy,
  bottomText,
  layout,
  background,
  cardBackground,
  cardBorder,
  cardPadding,
  cardMargin,
}: PropsWithChildren<{
  startScreen?: SlotComponent
  header: SlotComponent
  footer: SlotComponent
  headline?: string
  headlineStyle?: React.CSSProperties
  introCopy?: string
  bottomText?: string
  layout: ReferralLayout
  background?: BackgroundStyleProps
  cardBackground?: BackgroundStyleProps
  cardBorder?: BorderStyleProps
  cardPadding?: Spacing
  cardMargin?: Spacing
}>) => {
  const puck = usePuck()
  const isEditorial =
    puck?.appState?.data?.root?.props?.breakType === 'editorial'

  // Mirror production remainder classes from the stored card count.
  const content = (puck?.appState?.data?.content ?? []) as ComponentData[]
  const container = content.find(c => c.type === 'ReferralContainer')
  const cards =
    ((container?.props as ReferralContainerStoredProps | undefined)?.items ??
      []) as ComponentData[]
  const remainder = cardListRemainder(layout, cards.length)

  const mainLayers = splitBackgroundLayers(background)
  const cardLayers = splitBackgroundLayers(cardBackground)
  const cardStyle = {
    backgroundColor: 'transparent',
    ...cardLayers.base,
    ...applyBorderStyle(cardBorder ?? {}),
    ...buildCardSpacing(cardPadding, cardMargin),
  }

  return (
    <main
      className={isEditorial ? 'editorial container' : 'sponsored'}
      style={{ ...mainLayers.base, display: 'flow-root' }}
    >
      {mainLayers.overlay && <div style={mainLayers.overlay} />}
      {StartScreen && (
        <div style={{ marginBottom: 12 }}>
          <span style={slotLabelStyle}>Start Screen (optional)</span>
          <StartScreen allow={['PollStartScreen']} minEmptyHeight={80} />
        </div>
      )}
      <section className="referral-step">
        <div className="step-content with-shadow" style={cardStyle}>
          {cardLayers.overlay && <div style={cardLayers.overlay} />}
          {headline && (
            <h1
              className="step-content__title referral-title"
              style={headlineStyle}
              dangerouslySetInnerHTML={{ __html: headline }}
            />
          )}
          {introCopy && (
            <h2
              className="step-intro step-subcopy"
              dangerouslySetInnerHTML={{ __html: introCopy }}
            />
          )}
          <div style={{ marginBottom: 12 }}>
            <span style={slotLabelStyle}>Header (optional)</span>
            <Header disallow={REFERRAL_DISALLOW} minEmptyHeight={32} />
          </div>
          <span style={slotLabelStyle}>Cards</span>
          <div className={`layout-${layout}`}>{children}</div>
          {bottomText && (
            <div
              className="referral-bottom-text"
              dangerouslySetInnerHTML={{ __html: bottomText }}
            />
          )}
          <div style={{ marginTop: 12 }}>
            <span style={slotLabelStyle}>Footer (optional)</span>
            <Footer disallow={REFERRAL_DISALLOW} minEmptyHeight={32} />
          </div>
        </div>
      </section>
    </main>
  )
}

type ReferralContainerRenderProps = Omit<
  ReferralContainerProps,
  'header' | 'items' | 'footer' | 'startScreen'
> & {
  header: SlotComponent
  items: SlotComponent
  footer: SlotComponent
  startScreen: SlotComponent
}

export const PuckReferralContainer = (props: ReferralContainerRenderProps) => {
  const { items: Items, layout } = props
  const puckCtx = useContext(renderContext)
  const isRenderMode = puckCtx.data.content.length > 0

  if (isRenderMode) {
    return <PuckReferralContainerRenderer puckCtx={puckCtx} />
  }

  return (
    <PuckReferralEditorWrapper
      startScreen={props.startScreen}
      header={props.header}
      footer={props.footer}
      headline={props.headline}
      headlineStyle={buildHeadlineStyle(props)}
      introCopy={cleanRichtext(props.introCopy)}
      bottomText={cleanRichtext(props.bottomText)}
      layout={layout}
      background={props.background}
      cardBackground={props.cardBackground}
      cardBorder={props.cardBorder}
      cardPadding={props.cardPadding}
      cardMargin={props.cardMargin}
    >
      <Items
        allow={['ReferralCard']}
        className={layout === 'ONE_COLUMN' ? 'link-list' : 'card-list'}
        style={cardsSlotStyle(layout)}
        minEmptyHeight={80}
      />
    </PuckReferralEditorWrapper>
  )
}
